/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback, useMemo } from 'react';

import { cssMap, jsx } from '@compiled/react';
import { useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import LinkIcon from '@atlaskit/icon/core/link';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import {
	type ActionKeys,
	JIRA_PLANS_ADD_DEPENDENCY_KEY,
} from '@atlassian/conversation-assistant-message-actions-api/constants';
import type { JiraPlansAddDependencyPayload } from '@atlassian/conversation-assistant-message-actions-api/jira';
import {
	type ActionRendererFor,
	type ActionRendererKeyToPayload,
	type ActionWith,
} from '@atlassian/conversation-assistant-message-actions-api/types';
import { type MessageAction } from '@atlassian/conversation-assistant-service-api/types/message-actions';
import { isDefaultRovoChat } from '@atlassian/conversation-assistant-utils/default-agent';

import { ActionCardFooter, ActionCardWrapper } from '../../common/ui/action-card-wrapper';
import { buildToolFromAction } from '../../common/utils/buildToolFromAction';
import { jiraPlansActionData } from '../../common/utils/plan-action-data';

import messages from './messages';

const INTERACTION_SOURCE = 'jiraPlansAddDependencyRenderer';

const styles = cssMap({
	footer: {
		display: 'flex',
		justifyContent: 'flex-end',
		gap: token('space.100'),
	},
	cardWrapper: {
		paddingTop: token('space.050'),
		paddingRight: token('space.050'),
		paddingBottom: token('space.050'),
		paddingLeft: token('space.050'),
	},
	issueCard: {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
		gap: token('space.100'),
		paddingTop: token('space.100'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.150'),
		border: `${token('border.width')} solid ${token('color.border')}`,
		borderRadius: token('radius.large'),
	},
	issueTypeIcon: {
		width: '16px',
		height: '16px',
	},
	line: {
		width: '2px',
		height: '12px',
		backgroundColor: token('color.icon.subtle'),
	},
	iconCircle: {
		width: '20px',
		height: '20px',
		borderRadius: token('radius.full'),
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		backgroundColor: token('color.icon.subtle'),
		color: token('color.text.inverse'),
	},
	separatorColumn: {
		marginInlineStart: token('space.100'),
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
	},
});

export const filterAddDependencyActions = (
	actions: MessageAction[],
): ActionWith<ActionRendererKeyToPayload[typeof ActionKeys.JiraPlansAddDependency]>[] => {
	const filteredActions = actions.filter((action) => assertIsAddDependencyAction(action));
	return filteredActions as ActionWith<
		ActionRendererKeyToPayload[typeof ActionKeys.JiraPlansAddDependency]
	>[];
};

const assertIsAddDependencyAction = (action: MessageAction): MessageAction => {
	if (!jiraPlansActionData.isPlanActionKeyForRenderer(action.key, JIRA_PLANS_ADD_DEPENDENCY_KEY)) {
		throw new Error('Invalid action key for AddDependencyAction');
	}
	return action;
};

export const AddDependencyAction = (
	props: ActionRendererFor<typeof ActionKeys.JiraPlansAddDependency>,
): JSX.Element | null => {
	const { actions } = props;

	const firstAction = useMemo(() => filterAddDependencyActions(actions)[0], [actions]);
	if (!firstAction) {
		return null;
	}

	return <AddDependencyActionContent {...props} firstAction={firstAction} />;
};

const AddDependencyActionContent = (
	props: ActionRendererFor<typeof ActionKeys.JiraPlansAddDependency> & {
		firstAction: ActionWith<ActionRendererKeyToPayload[typeof ActionKeys.JiraPlansAddDependency]>;
	},
) => {
	const { sendAnalyticsEvent } = useAnalytics();
	const { formatMessage } = useIntl();

	const {
		getStatus,
		getReadonly,
		firstAction,
		onConfirm,
		onCancel,
		actions,
		sendMessageConfirmedTools,
		recipientAgentId,
		recipientAgentNamedId,
		conversationId,
		messageId,
	} = props;

	const isCustomAgent = !isDefaultRovoChat({
		agentId: recipientAgentId,
		agentNamedId: recipientAgentNamedId,
	});

	const { data } = firstAction;
	const actionData = jiraPlansActionData.getPlanActionData<JiraPlansAddDependencyPayload>(data);
	const readonly = getReadonly(firstAction);
	const status = getStatus(firstAction);
	const invocationStatus = String(status ?? '').toLowerCase();
	const actionStatus = String(firstAction.status ?? '').toLowerCase();

	const isActionLoading = invocationStatus !== 'idle';
	const isSubmitting = invocationStatus === 'started';
	const isActionFinished = invocationStatus === 'finished' || actionStatus === 'finished';
	const buttonsDisabled = isActionLoading || readonly;

	const handleConfirm = useCallback(() => {
		sendAnalyticsEvent({
			actionSubjectId: firstAction.actionId,
			action: 'clicked',
			actionSubject: 'button',
			attributes: {
				actionSubjectId: 'rovo-chat-jira-plans-add-dependency-confirm',
				planId: actionData.planId,
				scenarioId: actionData.scenarioId,
			},
		});

		if (
			fg('jira_plans_rovo_tools_resumption_migration') ||
			(isCustomAgent && fg('enable_skills_ga_tools_in_studio'))
		) {
			const idleActions = actions.filter((action) => getStatus(action) === 'idle');
			sendMessageConfirmedTools({
				agentId: recipientAgentId,
				conversationId,
				messageSources: { interactionSource: INTERACTION_SOURCE },
				confirmed_tools: {
					message_id: messageId ?? '',
					tools: idleActions.map((action) =>
						action.invocationId === firstAction.invocationId
							? buildToolFromAction(action, 'confirmed')
							: buildToolFromAction(action, 'cancelled'),
					),
				},
			});
			return;
		}

		onConfirm(firstAction);
	}, [
		firstAction,
		actions,
		actionData,
		getStatus,
		sendMessageConfirmedTools,
		recipientAgentId,
		conversationId,
		messageId,
		sendAnalyticsEvent,
		onConfirm,
		isCustomAgent,
	]);

	const handleCancel = useCallback(() => {
		sendAnalyticsEvent({
			actionSubjectId: firstAction.actionId,
			action: 'clicked',
			actionSubject: 'button',
			attributes: {
				actionSubjectId: 'rovo-chat-jira-plans-add-dependency-cancel',
				planId: actionData.planId,
				scenarioId: actionData.scenarioId,
			},
		});

		if (
			fg('jira_plans_rovo_tools_resumption_migration') ||
			(isCustomAgent && fg('enable_skills_ga_tools_in_studio'))
		) {
			const idleActions = actions.filter((action) => getStatus(action) === 'idle');
			sendMessageConfirmedTools({
				agentId: recipientAgentId,
				conversationId,
				messageSources: { interactionSource: INTERACTION_SOURCE },
				confirmed_tools: {
					message_id: messageId ?? '',
					tools: idleActions.map((action) => buildToolFromAction(action, 'cancelled')),
				},
			});
			return;
		}

		onCancel(firstAction);
	}, [
		firstAction,
		actions,
		actionData,
		getStatus,
		sendMessageConfirmedTools,
		recipientAgentId,
		conversationId,
		messageId,
		sendAnalyticsEvent,
		onCancel,
		isCustomAgent,
	]);

	const issueCard = useCallback(
		(itemKey: string, itemSummary?: string, itemTypeIcon?: string) => (
			<div css={styles.issueCard}>
				{itemTypeIcon ? <img src={itemTypeIcon} alt="" css={styles.issueTypeIcon} /> : null}
				<Text color="color.text.accent.blue" weight="semibold">
					{itemKey}
				</Text>
				{itemSummary ? <Text maxLines={1}>{itemSummary}</Text> : null}
			</div>
		),
		[],
	);

	const separator = useMemo(
		() => (
			<Inline space="space.100" alignBlock="center">
				<div css={styles.separatorColumn}>
					<div css={styles.line} />
					<div css={styles.iconCircle}>
						<LinkIcon label="" color={token('color.text.inverse')} />
					</div>
					<div css={styles.line} />
				</div>
				<Text color="color.text.subtlest">
					{actionData.dependencyTypeName ?? formatMessage(messages.dependencyTypeBlocks)}
				</Text>
			</Inline>
		),
		[actionData.dependencyTypeName, formatMessage],
	);

	if (!actionData.planId) {
		return null;
	}

	return (
		<ActionCardWrapper isExpandable title={formatMessage(messages.cardTitle)} product="plan">
			<div css={styles.cardWrapper}>
				<Stack alignInline="start" space="space.0">
					{issueCard(
						actionData.sourceItemKey,
						actionData.sourceItemSummary,
						actionData.sourceItemTypeIcon,
					)}
					{separator}
					{issueCard(
						actionData.targetItemKey,
						actionData.targetItemSummary,
						actionData.targetItemTypeIcon,
					)}
				</Stack>
			</div>

			{!isActionFinished && (
				<ActionCardFooter>
					<Box xcss={styles.footer}>
						<Button appearance="default" onClick={handleCancel} isDisabled={buttonsDisabled}>
							{formatMessage(messages.cancelButton)}
						</Button>
						<Button
							appearance="primary"
							onClick={handleConfirm}
							isDisabled={buttonsDisabled}
							isLoading={isSubmitting}
						>
							{formatMessage(messages.confirmButton)}
						</Button>
					</Box>
				</ActionCardFooter>
			)}
		</ActionCardWrapper>
	);
};
